import React, {
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { fetchCheckout } from "../redux/actions/checkoutAction";
import fetchCitiesAction from "../redux/actions/location/citiesActions";
import fetchWilayasAction from "../redux/actions/location/wilayasActions";
import fetchFeesAction from "../redux/actions/location/deliveryFeesActions";
import { scrollToTop } from "../shared/shared";
import ReactPixel from "react-facebook-pixel";
import { DELIVERY_DOMICILE, DELIVERY_STOP_DESK } from "../shared/constants";
import useRecaptcha from "../hooks/useRecaptcha";
import ReCAPTCHA from "react-google-recaptcha";
import "../css/Checkout/confirmOrder.css";
import OrderProductLoading from "../components/confirmOrder/OrderProductLoading";
import ImageLoading from "../components/ImageLoading";
import useWholesale from "../hooks/useWholesale";

const OrderProducts = lazy(() =>
  import("../components/confirmOrder/OrderProducts")
);
const OrderTotal = lazy(() => import("../components/confirmOrder/OrderTotal"));

const ConfirmOrderPage = (props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isMounted = useRef(false);

  const {
    handleChange,
    handleExpiration,
    executeRecaptcha,
    checkRecaptchaAsync,
    recaptchaRef,
    recaptchaError,
    recaptcha,
  } = useRecaptcha();

  const { getWhsalePrice } = useWholesale();
  // const cartTotal = (total, item) => total + item.price * item.qty;
  const cartTotal = (total, item) => {
    const whsalePrice = getWhsalePrice(
      item.qty,
      item.price,
      item?.special_price ? "" : item
    );
    return total + whsalePrice * item.qty;
  };

  const cartState = useSelector((state) => state.cart.getCart);
  const checkoutState = useSelector((state) => state.checkout);
  const wilayasState = useSelector((state) => state.location.wilayas);
  const citiesState = useSelector((state) => state.location.cities);
  const feesState = useSelector((state) => state.location.fees);
  const featuresState = useSelector((state) => state.features);
  const authUser = useSelector((state) => state.auth.authenticated.user);
  const settingsState = useSelector((state) => state.storeInfos);

  const [fullname, setFullname] = useState("");
  const [phone, setPhone] = useState("");
  const [phone2, setPhone2] = useState(null);
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState(null);
  const [wilaya, setWilaya] = useState(null);
  const [city, setCity] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [createAccount, setCreateAccount] = useState(false);
  const [gender, setGender] = useState("M");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [password, setPassword] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderFailure, setOrderFailure] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const lang = i18n.language;

  // Recaptcha
  useEffect(() => {
    if (settingsState?.store_infos?.recaptcha_key && window?.grecaptcha)
      window?.grecaptcha.reset(recaptchaRef?.current?.getWidgetId());
  }, [settingsState?.store_infos?.recaptcha_key, window?.grecaptcha]);

  const handleCheckout = (event) => {
    event.preventDefault();
    executeRecaptcha();
    dispatch(
      fetchCheckout({
        fullname,
        phone,
        phone2,
        address,
        wilaya: wilaya.id,
        city,
        createAccount,
        email,
        gender,
        password,
        password_confirmation: passwordConfirmation,
        recaptcha,
        paymentMethod,
        lang,
        deliveryMethod,
      })
    )
      .then((res) => {
        ReactPixel.track("Purchase", {
          fullname: fullname /* firstname + ' ' + lastname, */,
          wilaya: wilaya.id,
          city: city,
          address: address,
          value:
            cartState.items.reduce(cartTotal, 0) + (feesState?.fees?.length > 0)
              ? feesState.fees[0].delivery_price
              : 0,
          currency: settingsState.store_infos.currency ?? "",
          "payment methode": paymentMethod,
        });
        setOrderSuccess(true);
      })
      .catch((err) => {
        setOrderFailure(true);
        setTimeout(() => {
          setOrderFailure(false);
        }, 3000);
      });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (authUser) {
      setFullname(authUser.fullname);
      setPhone(authUser.phone);
      setAddress(authUser.address);
      setWilaya(authUser.wilaya);
      setCity(featuresState?.features?.includes("delivery") ? 0 : authUser.city.id);
    } else {
      setFullname("");
      setPhone("");
      setAddress("");
      setWilaya(null);
      setCity(0);
    }
  }, [authUser]);

  useEffect(() => {
    if (isMounted.current) {
      if (wilaya !== null) {
        dispatch(fetchCitiesAction(wilaya.id));
      }
    } else isMounted.current = true;
    setDeliveryMethod("");
  }, [wilaya]);

  useEffect(() => {
    if (featuresState?.features?.includes("delivery")) {
      dispatch(fetchFeesAction());
    } else {
      dispatch(fetchWilayasAction());
    }
  }, [featuresState?.features?.includes("delivery")]);

  return (
    <>
      {/* breadcrumb */}
      <div className="custom-breadcrumb">
        <div className="container">
          <ol className="breadcrumb ">
            <li className="breadcrumb-item">
              <Link to="/">{t("home")}</Link>
            </li>
            {document.body.dir === "rtl" && (
              <span className="breadcrumb-separator">/</span>
            )}
            <li className="breadcrumb-item active">{t("checkout")}</li>
          </ol>
        </div>
      </div>
      <section className="checkout-section">
        <div className="container">
          <div className="top-header">
            <h3>{t("checkout")}</h3>
          </div>
          {!orderSuccess ? (
            cartState?.items?.length > 0 ? (
              <div className="row">
                <div className="col-lg-9">
                  <div className="personal-info-wrapper">
                    <div className="items-incart-header d-flex align-items-center">
                      <a
                        href="/#"
                        data-toggle="collapse"
                        data-target="#personal-info-tab"
                      >
                        <h4 className="d-flex align-items-center">
                          1. {t("personal_informations")}
                        </h4>
                      </a>
                    </div>
                    <div
                      className="collapse show personal-info-tab"
                      id="personal-info-tab"
                    >
                      <div className="form-wrapper checkout-form-wrapper">
                        <form
                          className="form-group"
                          id="checkout-form"
                          name="checkout-form"
                          onSubmit={handleCheckout}
                        >
                          <div className="row">
                            {/* first name */}
                            <div className="col-md-6">
                              <div
                                className={`custom-input ${
                                  checkoutState.statusCode === 422 &&
                                  checkoutState.error["fullname"] &&
                                  "error-input"
                                }`}
                              >
                                <label>{t("fullname")}:</label>
                                <input
                                  type="text"
                                  value={fullname}
                                  onChange={(event) =>
                                    setFullname(event.target.value)
                                  }
                                />
                                {checkoutState.statusCode === 422 &&
                                  checkoutState.error["fullname"] &&
                                  checkoutState.error["fullname"].map((err) => (
                                    <div
                                      key={err}
                                      className={`${
                                        document.body.dir === "rtl" &&
                                        "text-right"
                                      }`}
                                    >
                                      <span className="custom-input-error-message ">
                                        {err}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                            {/* phone */}
                            <div className="col-md-6">
                              <div
                                className={`custom-input ${
                                  checkoutState.statusCode === 422 &&
                                  checkoutState.error["phone"] &&
                                  "error-input"
                                }`}
                              >
                                <label>{t("phone")}:</label>
                                <input
                                  type="tel"
                                  value={phone}
                                  onChange={(event) =>
                                    setPhone(event.target.value)
                                  }
                                />
                                {checkoutState.statusCode === 422 &&
                                  checkoutState.error["phone"] &&
                                  checkoutState.error["phone"].map((err) => (
                                    <div
                                      key={err}
                                      className={`${
                                        document.body.dir === "rtl" &&
                                        "text-right"
                                      }`}
                                    >
                                      <span className="custom-input-error-message ">
                                        {err}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                          {/* adresse */}
                          {featuresState?.features?.includes(
                            "order-address") && (
                            <div
                              className={`custom-input ${
                                checkoutState.statusCode === 422 &&
                                checkoutState.error["address"] &&
                                "error-input"
                              }`}
                            >
                              <label>{t("address")}:</label>
                              <input
                                type="text"
                                value={address}
                                onChange={(event) =>
                                  setAddress(event.target.value)
                                }
                              />
                              {checkoutState.statusCode === 422 &&
                                checkoutState.error["address"] &&
                                checkoutState.error["address"].map((err) => (
                                  <div key={err}>
                                    <span className="custom-input-error-message ">
                                      {err}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                          {/* wilaya and city */}
                          <div className="row mt-2">
                            {/* wilaya */}
                            {featuresState?.features?.includes("delivery") ? (
                              <div className="col-md-6 ">
                                <div className="custom-select-wrapper">
                                  <label className="mb-3">{t("wilaya")}:</label>
                                  <select
                                    className={`custom-select ${
                                      checkoutState.statusCode === 422 &&
                                      checkoutState.error["wilaya"] &&
                                      "error-input"
                                    }`}
                                    style={{ width: "100%" }}
                                    onChange={(event) => {
                                      setWilaya(
                                        feesState.fees[event.target.value] ??
                                          null
                                      );
                                    }}
                                  >
                                    <option value={-1}>
                                      {t("select_a_wilaya")}
                                    </option>
                                    {!feesState.loading &&
                                      feesState.fees.length > 0 &&
                                      feesState.fees.map((wilaya, id) => (
                                        <option value={id} key={wilaya.id}>
                                          {i18n.language === "ar"
                                            ? wilaya.ar_name
                                            : wilaya.fr_name}
                                        </option>
                                      ))}
                                  </select>
                                  {checkoutState.statusCode === 422 &&
                                    checkoutState.error["wilaya"] && (
                                      <div
                                        className={`${
                                          document.body.dir === "rtl" &&
                                          "text-right"
                                        }`}
                                      >
                                        <span className="custom-input-error-message">
                                          {checkoutState.error["wilaya"]}
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ) : (
                              <div className="col-md-6 ">
                                <div className="custom-select-wrapper">
                                  <label className="mb-3">{t("wilaya")}:</label>
                                  <select
                                    className={`custom-select ${
                                      checkoutState.statusCode === 422 &&
                                      checkoutState.error["wilaya"] &&
                                      "error-input"
                                    }`}
                                    style={{ width: "100%" }}
                                    value={wilaya?.id - 1 ?? -1}
                                    onChange={(event) => {
                                      setWilaya(
                                        wilayasState.wilayas[
                                          event.target.value
                                        ] ?? null
                                      );
                                    }}
                                  >
                                    <option value={-1}>
                                      {t("select_a_wilaya")}
                                    </option>
                                    {!wilayasState.loading &&
                                      wilayasState.wilayas.length > 0 &&
                                      wilayasState.wilayas.map((wilaya, id) => (
                                        <option value={id} key={wilaya.id}>
                                          {i18n.language === "ar"
                                            ? wilaya.ar_name
                                            : wilaya.fr_name}
                                        </option>
                                      ))}
                                  </select>
                                  {checkoutState.statusCode === 422 &&
                                    checkoutState.error["wilaya"] && (
                                      <div
                                        className={`${
                                          document.body.dir === "rtl" &&
                                          "text-right"
                                        }`}
                                      >
                                        <span className="custom-input-error-message">
                                          {checkoutState.error["wilaya"]}
                                        </span>
                                      </div>
                                    )}
                                </div>
                              </div>
                            )}
                            {/* city */}
                            <div className="col-md-6">
                              <div className="custom-select-wrapper">
                                <label className="mb-3">{t("city")}:</label>
                                <select
                                  className={`custom-select ${
                                    checkoutState.statusCode === 422 &&
                                    checkoutState.error["city"] &&
                                    "error-input"
                                  }`}
                                  style={{ width: "100%" }}
                                  value={city}
                                  onChange={(event) =>
                                    setCity(event.target.value)
                                  }
                                >
                                  <option value={0}>
                                    {t("select_a_city")}
                                  </option>
                                  {!citiesState.loading &&
                                    // citiesState.cities.length > 0 &&
                                    citiesState.cities.length > 0 && (featuresState?.features?.includes("delivery") ? feesState.fees.some(item => item?.id === wilaya?.id) : true ) &&   
                                    citiesState.cities.map((city) => (
                                      <option value={city.id} key={city.id}>
                                        {i18n.language === "ar"
                                          ? city.ar_name
                                          : city.fr_name}
                                      </option>
                                    ))}
                                </select>
                                {checkoutState.statusCode === 422 &&
                                  checkoutState.error["city"] && (
                                    <div
                                      className={`${
                                        document.body.dir === "rtl" &&
                                        "text-right"
                                      }`}
                                    >
                                      <span className="custom-input-error-message">
                                        {checkoutState.error["city"]}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                          {/*domicile and stopdesk */}
                          {featuresState?.features?.includes("delivery") &&
                            wilaya !== null && (
                              <>
                                <label
                                  className={`${
                                    document.body.dir === "rtl" && "text-right"
                                  }`}
                                  style={{
                                    display: "inline-block",
                                    width: "100%",
                                  }}
                                  htmlFor=""
                                >
                                  {t("Delivery")}
                                </label>
                                <div className="row mt-2">
                                  {wilaya.stopdesk_status === 1 && (
                                    <div className="col-md-6 col-12 col-sm-6">
                                      <div className="custom-select-wrapper">
                                        <fieldset>
                                          <div className="radio-buy">
                                            <input
                                              type="radio"
                                              name="delivery-buy-now"
                                              id="delivery-desk"
                                              value={DELIVERY_STOP_DESK}
                                              checked={
                                                deliveryMethod ===
                                                DELIVERY_STOP_DESK
                                              }
                                              onChange={() => {
                                                setDeliveryMethod(
                                                  DELIVERY_STOP_DESK
                                                );
                                              }}
                                            />
                                            <label htmlFor="delivery-desk">
                                              {t("Stop Desk")}
                                            </label>
                                          </div>
                                          <svg
                                            width="22"
                                            height="20"
                                            viewBox="0 0 22 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              fill-rule="evenodd"
                                              clip-rule="evenodd"
                                              d="M5.29872e-09 1C5.29872e-09 0.734784 0.105357 0.48043 0.292893 0.292893C0.48043 0.105357 0.734784 0 1 0H12C12.2652 0 12.5196 0.105357 12.7071 0.292893C12.8946 0.48043 13 0.734784 13 1V6H17C17.6566 6 18.3068 6.12933 18.9134 6.3806C19.52 6.63188 20.0712 7.00017 20.5355 7.46447C20.9998 7.92876 21.3681 8.47996 21.6194 9.08658C21.8707 9.69321 22 10.3434 22 11V15C22.0001 15.6437 21.7932 16.2705 21.4098 16.7876C21.0265 17.3048 20.487 17.6849 19.871 17.872C19.6876 18.477 19.3178 19.0086 18.8143 19.3909C18.3108 19.7732 17.6994 19.9867 17.0674 20.0009C16.4353 20.0151 15.815 19.8293 15.2948 19.4699C14.7747 19.1106 14.3814 18.5962 14.171 18H7.83C7.61962 18.5962 7.22629 19.1106 6.70616 19.4699C6.18602 19.8293 5.56566 20.0151 4.93363 20.0009C4.3016 19.9867 3.69021 19.7732 3.18673 19.3909C2.68324 19.0086 2.3134 18.477 2.13 17.872C1.51384 17.6851 0.97412 17.305 0.590566 16.7879C0.207012 16.2707 -3.82447e-05 15.6439 5.29872e-09 15V11H6C6.26522 11 6.51957 10.8946 6.70711 10.7071C6.89464 10.5196 7 10.2652 7 10C7 9.73478 6.89464 9.48043 6.70711 9.29289C6.51957 9.10536 6.26522 9 6 9H5.29872e-09V7H4C4.26522 7 4.51957 6.89464 4.70711 6.70711C4.89464 6.51957 5 6.26522 5 6C5 5.73478 4.89464 5.48043 4.70711 5.29289C4.51957 5.10536 4.26522 5 4 5H5.29872e-09V1ZM13 16H14.171C14.3687 15.4404 14.7279 14.9521 15.2032 14.5967C15.6785 14.2414 16.2485 14.0349 16.8411 14.0036C17.4337 13.9722 18.0223 14.1173 18.5325 14.4205C19.0426 14.7237 19.4513 15.1714 19.707 15.707C19.8 15.6143 19.8738 15.5041 19.924 15.3827C19.9743 15.2614 20.0001 15.1313 20 15V11C20 10.2044 19.6839 9.44129 19.1213 8.87868C18.5587 8.31607 17.7956 8 17 8H13V16ZM6 17C6 16.7348 5.89464 16.4804 5.70711 16.2929C5.51957 16.1054 5.26522 16 5 16C4.73478 16 4.48043 16.1054 4.29289 16.2929C4.10536 16.4804 4 16.7348 4 17C4 17.2652 4.10536 17.5196 4.29289 17.7071C4.48043 17.8946 4.73478 18 5 18C5.26522 18 5.51957 17.8946 5.70711 17.7071C5.89464 17.5196 6 17.2652 6 17ZM16.293 16.293C16.1999 16.3856 16.126 16.4958 16.0757 16.6172C16.0254 16.7385 15.9997 16.8686 16 17C16 17.2314 16.0801 17.4556 16.2269 17.6344C16.3736 17.8133 16.5778 17.9358 16.8047 17.981C17.0316 18.0261 17.2672 17.9912 17.4712 17.8822C17.6753 17.7732 17.8352 17.5968 17.9238 17.3831C18.0124 17.1693 18.0241 16.9315 17.957 16.7101C17.8899 16.4887 17.7481 16.2974 17.5557 16.1688C17.3634 16.0403 17.1324 15.9824 16.9021 16.005C16.6719 16.0277 16.4566 16.1294 16.293 16.293Z"
                                              fill="#A8A8A8"
                                            />
                                          </svg>
                                        </fieldset>
                                      </div>
                                    </div>
                                  )}
                                  {wilaya.domicile_status === 1 && (
                                    <div className="col-md-6 col-12 col-sm-6">
                                      <div className="custom-select-wrapper">
                                        <fieldset>
                                          <div className="radio-buy">
                                            <input
                                              type="radio"
                                              name="delivery-buy-now"
                                              id="delivery-home"
                                              value={DELIVERY_DOMICILE}
                                              checked={
                                                deliveryMethod ===
                                                DELIVERY_DOMICILE
                                              }
                                              onChange={() => {
                                                setDeliveryMethod(
                                                  DELIVERY_DOMICILE
                                                );
                                              }}
                                            />
                                            <label htmlFor="delivery-home">
                                              {t("Your Home")}
                                            </label>
                                          </div>
                                          <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M18.5 3H16C15.8674 3 15.7402 3.05268 15.6464 3.14645C15.5527 3.24021 15.5 3.36739 15.5 3.5V3.559L19 6.359V3.5C19 3.36739 18.9473 3.24021 18.8536 3.14645C18.7598 3.05268 18.6326 3 18.5 3Z"
                                              fill="#A8A8A8"
                                            />
                                            <path
                                              fill-rule="evenodd"
                                              clip-rule="evenodd"
                                              d="M10.75 9.5C10.75 9.16848 10.8817 8.85054 11.1161 8.61612C11.3505 8.3817 11.6685 8.25 12 8.25C12.3315 8.25 12.6495 8.3817 12.8839 8.61612C13.1183 8.85054 13.25 9.16848 13.25 9.5C13.25 9.83152 13.1183 10.1495 12.8839 10.3839C12.6495 10.6183 12.3315 10.75 12 10.75C11.6685 10.75 11.3505 10.6183 11.1161 10.3839C10.8817 10.1495 10.75 9.83152 10.75 9.5Z"
                                              fill="#A8A8A8"
                                            />
                                            <path
                                              fill-rule="evenodd"
                                              clip-rule="evenodd"
                                              d="M20.75 10.9599L21.532 11.5859C21.6877 11.7072 21.8849 11.7622 22.0809 11.7391C22.2769 11.716 22.4559 11.6165 22.5791 11.4623C22.7022 11.3081 22.7596 11.1115 22.7388 10.9153C22.7181 10.719 22.6207 10.5388 22.468 10.4139L14.343 3.91385C13.678 3.38172 12.8517 3.0918 12 3.0918C11.1483 3.0918 10.322 3.38172 9.657 3.91385L1.532 10.4139C1.37658 10.5381 1.27689 10.719 1.25486 10.9168C1.23282 11.1145 1.29025 11.3129 1.4145 11.4684C1.53875 11.6238 1.71966 11.7235 1.91741 11.7455C2.11517 11.7675 2.31358 11.7101 2.469 11.5859L3.25 10.9599V21.2499H2C1.80109 21.2499 1.61032 21.3289 1.46967 21.4695C1.32902 21.6102 1.25 21.8009 1.25 21.9999C1.25 22.1988 1.32902 22.3895 1.46967 22.5302C1.61032 22.6708 1.80109 22.7499 2 22.7499H22C22.1989 22.7499 22.3897 22.6708 22.5303 22.5302C22.671 22.3895 22.75 22.1988 22.75 21.9999C22.75 21.8009 22.671 21.6102 22.5303 21.4695C22.3897 21.3289 22.1989 21.2499 22 21.2499H20.75V10.9599ZM9.25 9.49985C9.25 8.77051 9.53973 8.07104 10.0555 7.55531C10.5712 7.03959 11.2707 6.74985 12 6.74985C12.7293 6.74985 13.4288 7.03959 13.9445 7.55531C14.4603 8.07104 14.75 8.77051 14.75 9.49985C14.75 10.2292 14.4603 10.9287 13.9445 11.4444C13.4288 11.9601 12.7293 12.2499 12 12.2499C11.2707 12.2499 10.5712 11.9601 10.0555 11.4444C9.53973 10.9287 9.25 10.2292 9.25 9.49985ZM12.05 13.2499C12.714 13.2499 13.287 13.2499 13.746 13.3119C14.238 13.3779 14.709 13.5269 15.091 13.9089C15.473 14.2909 15.622 14.7619 15.688 15.2539C15.746 15.6839 15.75 16.2139 15.75 16.8269V21.2499H14.25V16.9999C14.25 16.2719 14.248 15.7999 14.202 15.4539C14.158 15.1289 14.088 15.0269 14.03 14.9699C13.973 14.9129 13.871 14.8419 13.546 14.7979C13.199 14.7519 12.728 14.7499 12 14.7499C11.272 14.7499 10.8 14.7519 10.454 14.7979C10.129 14.8419 10.027 14.9129 9.97 14.9699C9.913 15.0269 9.842 15.1289 9.798 15.4539C9.752 15.8009 9.75 16.2719 9.75 16.9999V21.2499H8.25V16.9499C8.25 16.2859 8.25 15.7129 8.312 15.2539C8.378 14.7619 8.527 14.2909 8.909 13.9089C9.291 13.5269 9.763 13.3779 10.254 13.3119C10.713 13.2499 11.286 13.2499 11.951 13.2499H12.049H12.05Z"
                                              fill="#A8A8A8"
                                            />
                                            <path
                                              fill-rule="evenodd"
                                              clip-rule="evenodd"
                                              d="M10.75 9.5C10.75 9.16848 10.8817 8.85054 11.1161 8.61612C11.3505 8.3817 11.6685 8.25 12 8.25C12.3315 8.25 12.6495 8.3817 12.8839 8.61612C13.1183 8.85054 13.25 9.16848 13.25 9.5C13.25 9.83152 13.1183 10.1495 12.8839 10.3839C12.6495 10.6183 12.3315 10.75 12 10.75C11.6685 10.75 11.3505 10.6183 11.1161 10.3839C10.8817 10.1495 10.75 9.83152 10.75 9.5Z"
                                              fill="#A8A8A8"
                                            />
                                          </svg>
                                        </fieldset>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          {/* payment */}
                          {settingsState?.store_infos?.payment_information ===
                            "1" && (
                            <fieldset className="mt-3">
                              <div className="row radio-input">
                                <legend
                                  className="col-form-label col-sm-3 pt-0"
                                  style={{
                                    textAlign:
                                      document.body.dir === "rtl" && "right",
                                  }}
                                >
                                  {t("payment_method")} :
                                </legend>
                                <div
                                  className={`col-sm-9 ${
                                    document.body.dir === "rtl" && "text-right"
                                  }`}
                                >
                                  <label className="form-check-label mr-3">
                                    <input
                                      name="paymentMethod"
                                      value="CASH"
                                      className="mx-1 leading-tight"
                                      type="radio"
                                      checked={paymentMethod === "CASH"}
                                      onChange={() => setPaymentMethod("CASH")}
                                    />
                                    <span className="text-sm">{t("cash")}</span>
                                  </label>
                                  {(authUser || createAccount) && (
                                    <>
                                      <label className="form-check-label mr-3">
                                        <input
                                          name="paymentMethod"
                                          value="EDAHABIA"
                                          className="mx-1 leading-tight"
                                          type="radio"
                                          checked={paymentMethod === "EDAHABIA"}
                                          onChange={() =>
                                            setPaymentMethod("EDAHABIA")
                                          }
                                        />
                                        <span className="text-sm">
                                          {t("edahabia")}
                                        </span>
                                      </label>
                                      <label className="form-check-label mr-3">
                                        <input
                                          name="paymentMethod"
                                          value="CIB"
                                          className="mx-1 leading-tight"
                                          type="radio"
                                          checked={paymentMethod === "CIB"}
                                          onChange={() =>
                                            setPaymentMethod("CIB")
                                          }
                                        />
                                        <span className="text-sm">
                                          {t("cib")}
                                        </span>
                                      </label>
                                    </>
                                  )}
                                </div>
                              </div>
                            </fieldset>
                          )}
                          {!authUser && (
                            <div
                              className="form-group form-check mt-3"
                              style={{
                                textAlign:
                                  document.body.dir === "rtl" && "right",
                              }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="createAccount"
                                checked={createAccount}
                                onChange={() =>
                                  setCreateAccount(!createAccount)
                                }
                              />
                              <label
                                className="form-check-label"
                                style={{
                                  marginRight:
                                    document.body.dir === "rtl" && "1rem",
                                }}
                              >
                                {t("create_account")}
                              </label>
                            </div>
                          )}
                          {createAccount && (
                            <>
                              <div className="row">
                                <div className="col-md-6">
                                  <div
                                    className={`custom-input ${
                                      checkoutState.statusCode === 422 &&
                                      checkoutState.error["email"] &&
                                      "error-input"
                                    }`}
                                  >
                                    <label>{t("email")}:</label>
                                    <input
                                      type="email"
                                      value={email}
                                      onChange={(event) =>
                                        setEmail(event.target.value)
                                      }
                                    />
                                    {checkoutState.statusCode === 422 &&
                                      checkoutState.error["email"] &&
                                      checkoutState.error["email"].map(
                                        (err) => (
                                          <div
                                            key={err}
                                            className={`${
                                              document.body.dir === "rtl" &&
                                              "text-right"
                                            }`}
                                          >
                                            <span className="custom-input-error-message ">
                                              {err}
                                            </span>
                                          </div>
                                        )
                                      )}
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="custom-select-wrapper">
                                    <label className="mb-3">
                                      {t("gender")}:
                                    </label>
                                    <select
                                      className={`custom-select ${
                                        checkoutState.statusCode === 422 &&
                                        checkoutState.error["gender"] &&
                                        "error-input"
                                      }`}
                                      style={{ width: "100%" }}
                                      value={gender}
                                      onChange={(event) =>
                                        setGender(event.target.value)
                                      }
                                    >
                                      <option value="M">{t("male")}</option>
                                      <option value="F">{t("female")}</option>
                                    </select>
                                    {checkoutState.statusCode === 422 &&
                                      checkoutState.error["gender"] && (
                                        <span className="custom-input-error-message">
                                          {checkoutState.error["gender"]}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-md-6">
                                  <div
                                    className={`custom-input ${
                                      checkoutState.statusCode === 422 &&
                                      checkoutState.error["password"] &&
                                      "error-input"
                                    }`}
                                  >
                                    <label>{t("password")}:</label>
                                    <input
                                      type="password"
                                      value={password}
                                      onChange={(event) =>
                                        setPassword(event.target.value)
                                      }
                                    />
                                    {checkoutState.statusCode === 422 &&
                                      checkoutState.error["password"] &&
                                      checkoutState.error["password"].map(
                                        (err) => (
                                          <div
                                            key={err}
                                            className={`${
                                              document.body.dir === "rtl" &&
                                              "text-right"
                                            }`}
                                          >
                                            <span className="custom-input-error-message ">
                                              {err}
                                            </span>
                                          </div>
                                        )
                                      )}
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div
                                    className={`custom-input ${
                                      checkoutState.statusCode === 422 &&
                                      checkoutState.error[
                                        "password_confirmation"
                                      ] &&
                                      "error-input"
                                    }`}
                                  >
                                    <label>{t("password_confirmation")}:</label>
                                    <input
                                      type="password"
                                      value={passwordConfirmation}
                                      onChange={(event) =>
                                        setPasswordConfirmation(
                                          event.target.value
                                        )
                                      }
                                    />
                                    {checkoutState.statusCode === 422 &&
                                      checkoutState.error[
                                        "password_confirmation"
                                      ] &&
                                      checkoutState.error[
                                        "password_confirmation"
                                      ].map((err) => (
                                        <div key={err}>
                                          <span className="custom-input-error-message ">
                                            {err}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          {/* Recaptcha section */}
                          {settingsState?.store_infos?.recaptcha_validation == "1" &&
                          settingsState?.store_infos?.recaptcha_key && (
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={
                                settingsState?.store_infos?.recaptcha_key
                              }
                              size="invisible"
                              onChange={handleChange}
                              onExpired={handleExpiration}
                              onErrored={handleExpiration}
                              asyncScriptOnLoad={checkRecaptchaAsync}
                            />
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                  <Suspense
                    fallback={[...Array(2)].map((item, index) => (
                      <OrderProductLoading key={index} />
                    ))}
                  >
                    <OrderProducts />
                  </Suspense>
                </div>

                <Suspense
                  fallback={<ImageLoading width={"100%"} height={"200px"} />}
                >
                  <OrderTotal
                    deliveryMethod={deliveryMethod}
                    orderFailure={orderFailure}
                    fees={
                      featuresState?.features?.includes("delivery") &&
                      wilaya !== null &&
                      deliveryMethod !== ""
                        ? deliveryMethod === DELIVERY_DOMICILE
                        ? wilaya.delivery_price : wilaya.stopdesk_price : 0
                        //   ? feesState?.fees?.find(
                        //       (elem) => elem.id === wilaya.id
                        //     ).delivery_price
                        //   : feesState?.fees?.find(
                        //       (elem) => elem.id === wilaya.id
                        //     ).stopdesk_price
                        // : 0
                    }
                  />
                </Suspense>
              </div>
            ) : (
              <div className="row">
                <div className="col-md-12 justify-content-center">
                  <div className="row justify-content-center">
                    <div className="order-success-wrapper col-md-6 text-center">
                      <h3>{t("cart_items_count_zero")}</h3>
                      <div className="mb-3">
                        <i className="fa fa-times" />
                      </div>
                      <Link to="/shop" className="mt-3">
                        <button
                          className="submit-btn"
                          aria-label="Continue shopping"
                        >
                          {document.body.dir === "ltr" ? (
                            <i className="fa fa-chevron-left mr-2"></i>
                          ) : (
                            <i className="fa fa-chevron-right ml-2"></i>
                          )}
                          {t("continue_shopping")}
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="row">
              <div className="col-md-12 justify-content-center">
                <h3 className="text-center login-form-header">
                  {t("order_was_successful")}
                </h3>
                <div className="row justify-content-center m-0 ">
                  <div className="order-success-wrapper col-12 text-center">
                    <h3>{t("congratulation_your_order_sucessful")}</h3>
                    <div className="mb-3">
                      <i className="fa fa-check" />
                    </div>
                    <Link to="/shop" className="mt-3">
                      <button
                        className="submit-btn"
                        aria-label="Continue shopping"
                      >
                        {document.body.dir === "ltr" ? (
                          <i className="fa fa-chevron-left mr-2"></i>
                        ) : (
                          <i className="fa fa-chevron-right ml-2"></i>
                        )}
                        {t("continue_shopping")}
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ConfirmOrderPage;
